import { useMutation, useQuery } from '@tanstack/react-query'

import type {
    AccountSingleStepPinRequest,
    AddressDetailsRequest,
    CancelTransactionRequest,
    CardPaymentPrepareRequest,
    CardPaymentResolvedRequest,
    ChangePinRequest,
    ConnectionStatusRequest,
    ContractPreliminaryRequest,
    ContractSignOrBuyRequest,
    CreatePinRequest,
    DocumentType,
    EmploymentInfoRequest,
    FinancialAccountNumberV2Request,
    FinancialDetailsRequestV2,
    FinancialProductV2Request,
    JWTLoginRequest,
    MaritalStatusRequest,
    NationalityCodebookDTO,
    PaymentData,
    PaymentDataRequest,
    PersonalDetailsCheckRequest,
    PersonalDetailsV2Request,
    PersonMarketingConsentChangeRequest,
    ProfileAddressChangeRequest,
    ProfileIbanChangeRequest,
    Psd2ConnectionStatusRequest,
    Psd2TokenRequest,
    ReCreatePinRequest,
    RequestFinancialDataRequest,
    SessionInfoDTO,
    SettlementCodebookDTO,
    SubmitAdditionalDocumentValidationRequest,
    TransactionRequest,
    ValidationDataV2DTO,
    VendorRequestInvalidateRequest,
} from '@leanpay/models'
import type { KYCSubmitCheckPayload } from '@leanpay/services'
import {
    CheckoutRequestStatus,
    sessionInfoSelectors,
    toCheckoutData,
    UpsellOfferState,
} from '@leanpay/models'
import {
    createStandardRetryHandler,
    useGetCountries as useGetCountriesShared,
    useGetPostZipsByCountry as useGetPostZipsByCountryShared,
} from '@leanpay/query-hooks'
import {
    checkoutService,
    creditCardService,
    downloadService,
    getAuthToken,
    kycService,
    psd2Service,
    saveBlobFile,
    storeTokenAsync,
} from '@leanpay/services'
import { SelectOptionOther } from '@leanpay/utils'

import { queryClient } from 'app/app'
import { useAppStore } from 'app/app-store'
import {
    CacheKey,
    countryCodebookId,
    isMarketHun,
    isMarketSlo,
    STATE_MACHINE_TERMINATED,
    UPSELL_OFFER_POLL_INTERVAL,
} from 'library/constants'

/**
 * Reference-data and codebook hooks shared with pre-qualify and self-checkout.
 * Only hooks that need this app's market constant are wrapped here.
 */
export {
    useGetCurrency,
    useGetCurrentTime,
    useGetEmploymentTypes,
    useGetExecutionAmounts,
    useGetIndustriesOfEmployment,
    useGetMaritalStatuses,
    useGetNumberOfConsecutivePaymentMonths,
    useGetNumberOfRecentLoans,
    useGetPostZipsBySettlement,
    useGetSettlementsByPostZip,
    useGetSettlementsByStreet,
    useGetStreetsByPostZip,
    useGetStreetsBySettlement,
    useLoadMaritalStatus,
} from '@leanpay/query-hooks'

export const useGetCountries = () => useGetCountriesShared(countryCodebookId)
export const useGetPostZipsByCountry = () => useGetPostZipsByCountryShared(countryCodebookId)

export const useGetVendorTokenCheckout = () => {
    const mutation = useMutation({
        mutationFn: (vendorMockToken: string) =>
            checkoutService.getVendorTokenCheckout(vendorMockToken),
    })

    return mutation
}

// Polls the upsell offer-state endpoint while the offer is PENDING; stops on a terminal state.
export const useGetUpsellOfferState = ({ token, enabled }: { token: string; enabled: boolean }) => {
    return useQuery({
        queryKey: [CacheKey.UpsellOfferState, token],
        queryFn: () => checkoutService.getUpsellOfferState(token),
        enabled: !!token && enabled,
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
        refetchInterval: (query) => {
            const data = query.state.data
            if (!data) return UPSELL_OFFER_POLL_INTERVAL

            return data.state === UpsellOfferState.PENDING ? UPSELL_OFFER_POLL_INTERVAL : false
        },
    })
}

interface UpsellHandoffEntryVariables {
    offerToken: string
    fallbackUrl: string | null
}

// Both round-trips live in one mutation so a single isPending disables the CTAs for the whole
// entry, and a failure in either one degrades to the plain offer link — the pre-handoff flow.
export const useUpsellHandoffEntry = () => {
    const mutation = useMutation({
        mutationFn: async ({ offerToken }: UpsellHandoffEntryVariables) => {
            const { token: gplVendorToken } = await checkoutService.upsellCheckoutInit(offerToken)

            return checkoutService.issueHandoff(gplVendorToken)
        },
        onSuccess: (res) => {
            window.location.href = res.redirectUrl
        },
        onError: (_error, variables) => {
            if (variables.fallbackUrl) {
                window.location.href = variables.fallbackUrl
            }
        },
    })

    return mutation
}

export const useGetPaymentData = () => {
    const mutation = useMutation({
        mutationFn: (data: PaymentDataRequest & { needToChangePaymentOptions: boolean }) => {
            const cacheKey = [
                CacheKey.PaymentData,
                data.firstPaymentDate,
                data.maturityType,
                data.insurancePlansOnly,
                data.needToChangePaymentOptions,
            ]

            // Check if we already have this result cached
            // eslint-disable-next-line
            const cached = queryClient.getQueryData(cacheKey) as PaymentData
            // eslint-disable-next-line
            if (cached) {
                return Promise.resolve<PaymentData>(cached)
            }
            return checkoutService.getPaymentData(data)
        },
        onSuccess: (data, variables) => {
            const cacheKey = [
                CacheKey.PaymentData,
                // If firstPaymentDate is not provided, use the first date from the response for caching, so that subsequent calls with same params hit the cache
                // eslint-disable-next-line
                variables.firstPaymentDate || data.paymentCycleDates[0],
                variables.maturityType,
                variables.insurancePlansOnly,
                variables.needToChangePaymentOptions,
            ]

            queryClient.setQueryData(cacheKey, data)
        },
        retry: createStandardRetryHandler(),
        retryDelay: 500,
    })

    return mutation
}

export const useDownloadInitialOffer = () => {
    const mutation = useMutation({
        mutationFn: (data: TransactionRequest) => downloadService.downloadInitialOffer(data),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })

    return mutation
}
export const useDownloadPreliminaryContract = () => {
    const mutation = useMutation({
        mutationFn: (data: ContractPreliminaryRequest) =>
            downloadService.downloadPreliminaryContract(data),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })

    return mutation
}

export const useDownloadPrecontractualPeriodWaiver = () => {
    const mutation = useMutation({
        mutationFn: (data: ContractPreliminaryRequest) =>
            downloadService.downloadPrecontractualPeriodWaiver(data),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })

    return mutation
}

export const useDownloadInsuranceDocument = () => {
    const mutation = useMutation({
        mutationFn: (token: string) => downloadService.downloadInsuranceDocument(token),
        onSuccess: (data: { blob: Blob; fileName: string }) =>
            saveBlobFile(data.blob, data.fileName),
    })

    return mutation
}

export const useRequestPIN = () => {
    const mutation = useMutation({
        mutationFn: async (data: AccountSingleStepPinRequest) => {
            return checkoutService.requestPIN(data)
        },
    })

    return mutation
}

export const useResendOtpOverInvoice = () => {
    const mutation = useMutation({
        mutationFn: (phoneNumber: string) => {
            return checkoutService.resendOtpOverVoice(phoneNumber)
        },
    })

    return mutation
}
export const useResendOtp = () => {
    const mutation = useMutation({
        mutationFn: (phoneNumber: string) => {
            return checkoutService.resendOtp(phoneNumber)
        },
    })

    return mutation
}

export const useUpdateIban = (twoFaPin: string) => {
    const mutation = useMutation({
        mutationFn: (payload: ProfileIbanChangeRequest) =>
            checkoutService.ibanUpdate(payload, twoFaPin),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useUpdateAddress = () => {
    const mutation = useMutation({
        mutationFn: (payload: ProfileAddressChangeRequest) =>
            checkoutService.addressUpdate(payload),

        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useUpdateMarketingConsent = () => {
    const mutation = useMutation({
        mutationFn: (payload: PersonMarketingConsentChangeRequest) =>
            checkoutService.changeMarketingConsentLoginAction(payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}

export const useAuthenticate = () => {
    const { setIsAuthenticated } = useAppStore()
    const mutation = useMutation({
        mutationFn: async (data: JWTLoginRequest) => {
            return checkoutService.authenticate(data)
        },
        onSuccess: async (res) => {
            await storeTokenAsync(res.idToken)
            await queryClient.fetchQuery({ queryKey: [CacheKey.SessionInfo] })
            setIsAuthenticated(true)
        },
    })

    return mutation
}

export const useCheckPersonalDetails = () => {
    const mutation = useMutation({
        mutationFn: (data: PersonalDetailsCheckRequest) => {
            return checkoutService.checkPersonalDetails(data)
        },
        retry: createStandardRetryHandler(),
        retryDelay: 500,
    })
    return mutation
}
export const useSubmitPersonalDetails = () => {
    const mutation = useMutation({
        mutationFn: (data: PersonalDetailsV2Request) => {
            return checkoutService.submitPersonalDetails(data)
        },
        retryDelay: 500,
        retry: createStandardRetryHandler(),
    })

    return mutation
}
export const useSubmitAddressDetails = () => {
    const mutation = useMutation({
        mutationFn: (data: AddressDetailsRequest) => {
            return checkoutService.submitAddressDetailsV2(data)
        },
        retry: createStandardRetryHandler(),
        retryDelay: 500,
    })

    return mutation
}

export const useGetNationalities = () => {
    return useQuery({
        queryKey: [CacheKey.Nationality],
        queryFn: () => checkoutService.getNationalityCodebook(),
        select: (nationalities: any[]) => {
            const targetedNationality = nationalities.find(
                (n: NationalityCodebookDTO) => n.country.toString().toUpperCase() === 'HUNGARY',
            )
            const sortedNationalites = targetedNationality
                ? [
                      targetedNationality,
                      ...nationalities.filter(
                          (n: NationalityCodebookDTO) =>
                              n.country.toString().toUpperCase() !== 'HUNGARY',
                      ),
                  ]
                : nationalities
            return sortedNationalites
        },
    })
}

export const useLoadPersonalDetails = () => {
    const { vendorToken } = useAppStore()
    const mutation = useMutation({
        mutationFn: () => checkoutService.loadPersonalDetailsData(vendorToken),
    })

    return mutation
}

export const useLoadAddressDetails = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.AddressDetails],
        queryFn: async () => checkoutService.loadAddressDetails(vendorToken),
        retry: createStandardRetryHandler(),
    })
}

export const useLoadAddressDetailsMutation = () => {
    const { vendorToken } = useAppStore()
    const mutation = useMutation({
        mutationFn: () => checkoutService.loadAddressDetails(vendorToken),
    })

    return mutation
}

export const useLoadFinancialDetails = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.FinancialDetails],
        queryFn: async () => checkoutService.loadFinancialDetails(vendorToken),
        retry: createStandardRetryHandler(),
    })
}

export const useMutateTriggerWatchList = () => {
    const { vendorToken } = useAppStore()
    const mutation = useMutation({
        mutationFn: () => checkoutService.triggerWatchListCheck(vendorToken),
    })

    return mutation
}

export const useGetStateMachineStatus = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.StateMachineStatus],
        queryFn: () => checkoutService.getStateMachineStatus(vendorToken),
        refetchInterval: 3000,
    })
}

const CHECKOUT_DECISION_POLL_INTERVAL = 3000

/**
 * Polls the checkout decision endpoint for the POS final screen.
 * Stops polling once the state machine is TERMINATED or a final
 * APPROVED/REJECTED result is provided; keeps polling for pending /
 * ADDITIONAL_REVIEW states.
 */
export const useGetCheckoutStateMachineStatus = ({ enabled }: { enabled: boolean }) => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.CheckoutStateMachineStatus, vendorToken],
        queryFn: () => checkoutService.getCheckoutStateMachineStatus(vendorToken),
        enabled: !!vendorToken && enabled,
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
        refetchInterval: (query) => {
            const data = query.state.data
            if (!data) return CHECKOUT_DECISION_POLL_INTERVAL

            const isTerminated = data.additionalInfo.stateMachineState === STATE_MACHINE_TERMINATED
            const isFinalDecision =
                data.checkoutResultProvided &&
                (data.checkoutRequestStatus === CheckoutRequestStatus.APPROVED ||
                    data.checkoutRequestStatus === CheckoutRequestStatus.REJECTED)

            return isTerminated || isFinalDecision ? false : CHECKOUT_DECISION_POLL_INTERVAL
        },
    })
}

export const useConfirmCheckoutDecision = () => {
    const mutation = useMutation({
        mutationFn: (token: string) => checkoutService.confirmStateMachineFailure(token),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [CacheKey.CheckoutStateMachineStatus],
            })
        },
    })

    return mutation
}

export const useGetSettlements = () => {
    return useQuery({
        queryKey: [CacheKey.Settlement, countryCodebookId],
        enabled: !!countryCodebookId,
        queryFn: async () => {
            const settlements = await checkoutService.getSettlements(countryCodebookId)
            if (isMarketHun) {
                const data = [
                    ...settlements,
                    { id: 0, name: SelectOptionOther.OtherHUN, code: '0' } as SettlementCodebookDTO,
                ]
                return data
            }

            if (isMarketSlo) {
                // Remove duplicated items
                const uniqueItems = new Map()
                const updatedSettlements = settlements.filter((item) => {
                    if (!uniqueItems.has(item.name)) {
                        uniqueItems.set(item.name, true)
                        return true
                    }
                    return false
                })

                return updatedSettlements
            }

            return settlements
        },
    })
}
export const useRequestFinancialData = () => {
    const mutation = useMutation({
        mutationFn: (data: RequestFinancialDataRequest) =>
            checkoutService.requestFinancialData(data),
    })

    return mutation
}

export const useCreatePin = () => {
    const mutation = useMutation({
        mutationFn: (payload: CreatePinRequest) => {
            return checkoutService.createPin(payload)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useRecreatePin = () => {
    const mutation = useMutation({
        mutationFn: (payload: ReCreatePinRequest) => checkoutService.recreatePin(payload),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}
export const useChangePin = () => {
    const mutation = useMutation({
        mutationFn: (payload: ChangePinRequest) => checkoutService.changePin(payload),
    })

    return mutation
}

export const useSubmitMartialStatus = () => {
    const mutation = useMutation({
        mutationFn: (payload: MaritalStatusRequest) => checkoutService.submitMaritalStatus(payload),
        retry: createStandardRetryHandler(),
        retryDelay: 500,
    })

    return mutation
}

export const useSubmitEmploymentInfo = () => {
    const mutation = useMutation({
        mutationFn: (payload: EmploymentInfoRequest) =>
            checkoutService.submitEmploymentInfo(payload),
        retry: createStandardRetryHandler(),
        retryDelay: 500,
    })

    return mutation
}

export const useValidateOfferV2 = () => {
    const mutation = useMutation({
        mutationFn: (data: ValidationDataV2DTO) => {
            return checkoutService.validateCheckoutOfferV2(data)
        },
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
        },
    })

    return mutation
}

export const useGetContractData = () => {
    const mutation = useMutation({
        mutationFn: (payload: TransactionRequest) => checkoutService.getContractData(payload),
    })

    return mutation
}

export const useGetPepQuestionnaire = () => {
    return useQuery({
        queryKey: [CacheKey.PEPQuestionnaire],
        queryFn: () => checkoutService.getPepQuestionnaire(),
    })
}
export const useGetPepQuestionnaireROM = () => {
    return useQuery({
        queryKey: [CacheKey.PEPQuestionnaire],
        queryFn: () => checkoutService.getPepQuestionnaireROM(),
    })
}
export const useGetPepStatus = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.PepStatus],
        queryFn: () => checkoutService.getPepStatus(vendorToken),
    })
}

export const useGetSessionInfo = () => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.SessionInfo],
        enabled: !!vendorToken && !!getAuthToken(),
        queryFn: () => checkoutService.getSessionInfo(),
        refetchOnWindowFocus: false,
        retry: createStandardRetryHandler(),
    })
}

export const useSignOrBuy = () => {
    const mutation = useMutation({
        mutationFn: ({
            payload,
            twoFaPin,
        }: {
            payload: ContractSignOrBuyRequest
            twoFaPin?: string
        }) => checkoutService.signOrBuy(payload, twoFaPin),
    })

    return mutation
}

export const useDownloadGlobalDocument = (documentType: DocumentType) => {
    return useQuery({
        queryKey: [CacheKey.GlobalDocument, documentType],
        queryFn: () => downloadService.downloadGlobalDocument(documentType),
    })
}

export const useGetCreditCardStatus = (cardPaymentId: string) => {
    return useQuery({
        queryKey: [CacheKey.CreditCardStatus, cardPaymentId],
        queryFn: () => creditCardService.getCreditCardStatus(cardPaymentId),
    })
}

export const usePrepareCreditCard = () => {
    const mutation = useMutation({
        mutationFn: (payload: CardPaymentPrepareRequest) =>
            creditCardService.prepareCreditCard(payload),
    })

    return mutation
}

export const useResolvePaymentStatus = () => {
    const mutation = useMutation({
        mutationFn: (payload: CardPaymentResolvedRequest) =>
            creditCardService.resolveCreditCard(payload),
    })

    return mutation
}
export const useInvalidateVendorRequest = () => {
    const mutation = useMutation({
        mutationFn: (payload: VendorRequestInvalidateRequest) =>
            checkoutService.invalidateVendorRequest(payload),
    })

    return mutation
}

export const useGetKycStatus = (sessionInfo?: SessionInfoDTO) => {
    const { vendorToken } = useAppStore()
    return useQuery({
        queryKey: [CacheKey.KYCStatus],
        queryFn: () => kycService.getCheckStatus({ token: vendorToken }),
        refetchInterval: 4000,
        enabled: !(sessionInfo && sessionInfoSelectors.isVerified(sessionInfo)), //Disable checking if user is already verified
    })
}

export const useGetSdkToken = () => {
    const { vendorToken } = useAppStore()
    const mutation = useMutation({
        mutationFn: () => kycService.getSdkToken(vendorToken),
    })

    return mutation
}

export const useKYCSubmitCheck = () => {
    const mutation = useMutation({
        mutationFn: (payload: KYCSubmitCheckPayload) => kycService.submitCheck(payload),
    })

    return mutation
}

export const useInitFinancialProduct = () => {
    const mutation = useMutation({
        mutationFn: (request: FinancialProductV2Request) =>
            checkoutService.financialProductInit(request),
        retry: createStandardRetryHandler(),
        retryDelay: 500,
    })

    return mutation
}
export const useCheckBankAccountNumber = () => {
    const mutation = useMutation({
        mutationFn: (request: FinancialAccountNumberV2Request) =>
            checkoutService.checkBankAccountNumber(request),
        retry: createStandardRetryHandler(),
        retryDelay: 500,
    })

    return mutation
}
export const useSubmitFinancialDetails = () => {
    const mutation = useMutation({
        mutationFn: (request: FinancialDetailsRequestV2) =>
            checkoutService.submitFinancialDetails(request),
    })

    return mutation
}

//PSD2 - AIS HUN

export const useRequestPSD2Token = () => {
    const mutation = useMutation({
        mutationFn: (payload: Psd2TokenRequest) => psd2Service.requestToken(payload),
    })

    return mutation
}

export const useSendPSD2ConnectionStatus = () => {
    const mutation = useMutation({
        mutationFn: (payload: ConnectionStatusRequest) => psd2Service.sendConnectionStatus(payload),
    })
    return mutation
}

export const useGetPSD2ConnectionStatus = () => {
    const {
        setCheckoutData,
        setPaymentData,

        setVendorToken,
        setFinancialDetailsDataV2,
        setIsAuthenticated,
    } = useAppStore()

    const mutation = useMutation({
        mutationFn: (payload: Psd2ConnectionStatusRequest) =>
            psd2Service.getConnectionStatus(payload),
        onSuccess: async (res) => {
            if (res?.data) {
                const data = JSON.parse(res.data)
                setCheckoutData(
                    toCheckoutData({
                        ...data.checkoutData,
                    }),
                )
                setIsAuthenticated(true)

                await queryClient.invalidateQueries({ queryKey: [CacheKey.SessionInfo] })
                setVendorToken(data.token)
                setPaymentData(data.paymentData)
                setFinancialDetailsDataV2(data.financialDetailsDataV2)
                return
            }
        },
    })

    return mutation
}

export const useSubmitAdditionalDocument = () => {
    const mutation = useMutation({
        mutationFn: (data: SubmitAdditionalDocumentValidationRequest) => {
            return kycService.submitAdditionalDocument(data)
        },
    })

    return mutation
}

export const useCancelTransaction = () => {
    const mutation = useMutation({
        mutationFn: (data: CancelTransactionRequest) => {
            return checkoutService.cancelTransaction(data)
        },
    })

    return mutation
}
